/* generated using openapi-typescript-codegen -- do not edit */
/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AgentInviteResponse } from '../models/AgentInviteResponse';
import type { AgentOnlineDto } from '../models/AgentOnlineDto';
import type { AgentOnlineResponse } from '../models/AgentOnlineResponse';
import type { AgentReadinessResponse } from '../models/AgentReadinessResponse';
import type { AgentResponse } from '../models/AgentResponse';
import type { AgentSectorsResponse } from '../models/AgentSectorsResponse';
import type { AssignSectorsDto } from '../models/AssignSectorsDto';
import type { AuthResponse } from '../models/AuthResponse';
import type { CompleteAgentRegistrationDto } from '../models/CompleteAgentRegistrationDto';
import type { CreateAgentInviteDto } from '../models/CreateAgentInviteDto';
import type { DocumentResponse } from '../models/DocumentResponse';
import type { MessageResponse } from '../models/MessageResponse';
import type { PaginatedAgentListItemResponse } from '../models/PaginatedAgentListItemResponse';
import type { RefuseAgentDocumentDto } from '../models/RefuseAgentDocumentDto';
import type { RegisterSelfAsAgentDto } from '../models/RegisterSelfAsAgentDto';
import type { UpdateAgentDto } from '../models/UpdateAgentDto';
import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';
export class AgentsService {
    constructor(public readonly httpRequest: BaseHttpRequest) {}
    /**
     * Invite an agent
     * Invite a new agent to a society. Creates an AgentProfile with AWAITING_REGISTRATION status. Requires pre-uploaded document URLs (proCardUrl, idCardUrl). Upload files first via /uploads/documents. Sends an invitation email with a 6-digit validation code.
     * @returns AgentInviteResponse Agent invited successfully.
     * @throws ApiError
     */
    public agentsControllerInvite({
        societyId,
        requestBody,
    }: {
        /**
         * Society unique identifier
         */
        societyId: string,
        requestBody: CreateAgentInviteDto,
    }): CancelablePromise<AgentInviteResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/api/societies/{societyId}/agents/invite',
            path: {
                'societyId': societyId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Validation error.`,
                401: `Unauthorized.`,
                403: `Forbidden. SOCIETY_OWNER role required.`,
                404: `Society not found.`,
                409: `Agent already exists for this society.`,
            },
        });
    }
    /**
     * Resend agent invitation email
     * Resend the invitation email to an agent with AWAITING_REGISTRATION status. Generates a new validation code and extends the expiration by 7 days. Only the society owner can resend invites.
     * @returns AgentInviteResponse Invitation email resent successfully.
     * @throws ApiError
     */
    public agentsControllerResendInvite({
        societyId,
        agentId,
    }: {
        /**
         * Society unique identifier
         */
        societyId: string,
        /**
         * Agent profile unique identifier
         */
        agentId: string,
    }): CancelablePromise<AgentInviteResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/api/societies/{societyId}/agents/{agentId}/resend-invite',
            path: {
                'societyId': societyId,
                'agentId': agentId,
            },
            errors: {
                400: `Agent is not in AWAITING_REGISTRATION status.`,
                401: `Unauthorized.`,
                403: `Forbidden. SOCIETY_OWNER role required.`,
                404: `Agent or society not found.`,
            },
        });
    }
    /**
     * Complete agent registration
     * Complete agent registration by verifying the Firebase ID token. The agent is matched by the email in the token against pending invite. Links the user to the AgentProfile, creates a UserProfile, sets status to OPERATIONAL, and returns JWT tokens.
     * @returns AuthResponse Agent registration completed successfully.
     * @throws ApiError
     */
    public agentsControllerCompleteRegistration({
        requestBody,
    }: {
        requestBody: CompleteAgentRegistrationDto,
    }): CancelablePromise<AuthResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/api/agents/complete-registration',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Validation error or email mismatch.`,
                401: `Invalid Firebase ID token.`,
                404: `No pending agent invite found for this email.`,
            },
        });
    }
    /**
     * Check agent readiness status
     * Returns the agent readiness check: status, society validation, document validation, and sector assignment.
     * @returns AgentReadinessResponse Readiness status.
     * @throws ApiError
     */
    public agentsControllerGetReadiness(): CancelablePromise<AgentReadinessResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/agents/me/readiness',
            errors: {
                401: `Unauthorized.`,
                403: `Forbidden. AGENT role required.`,
                404: `Agent profile not found.`,
            },
        });
    }
    /**
     * List agents for a society
     * Returns a paginated list of agents for a given society. Only the society owner or an admin can view agents.
     * @returns PaginatedAgentListItemResponse Paginated list of agents.
     * @throws ApiError
     */
    public agentsControllerFindBySociety({
        societyId,
        page = 1,
        limit = 20,
        sortBy,
        sortOrder = 'desc',
        isOnline,
    }: {
        /**
         * Society unique identifier
         */
        societyId: string,
        /**
         * Page number
         */
        page?: number,
        /**
         * Items per page (max 200)
         */
        limit?: number,
        /**
         * Field to sort by
         */
        sortBy?: string,
        /**
         * Sort order (asc/desc)
         */
        sortOrder?: 'asc' | 'desc',
        /**
         * Filter by online status
         */
        isOnline?: boolean,
    }): CancelablePromise<PaginatedAgentListItemResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/societies/{societyId}/agents',
            path: {
                'societyId': societyId,
            },
            query: {
                'page': page,
                'limit': limit,
                'sortBy': sortBy,
                'sortOrder': sortOrder,
                'isOnline': isOnline,
            },
            errors: {
                401: `Unauthorized.`,
                403: `Forbidden.`,
            },
        });
    }
    /**
     * Self-register as agent
     * Allow an authenticated SOCIETY_OWNER to register themselves as an agent in their own society. Creates an AgentProfile with DOCUMENTS_PENDING status. Requires proCard and idCard document URLs (upload via /uploads/documents first).
     * @returns AgentResponse Agent profile created successfully.
     * @throws ApiError
     */
    public agentsControllerRegisterSelf({
        requestBody,
    }: {
        requestBody: RegisterSelfAsAgentDto,
    }): CancelablePromise<AgentResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/api/agents/me',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Validation error.`,
                401: `Unauthorized.`,
                403: `Forbidden. SOCIETY_OWNER role required or not the society owner.`,
                404: `Society not found.`,
                409: `Agent profile already exists for this society.`,
            },
        });
    }
    /**
     * Get current agent profile
     * Returns the authenticated agent's own profile.
     * @returns AgentResponse Agent profile.
     * @throws ApiError
     */
    public agentsControllerGetMe(): CancelablePromise<AgentResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/agents/me',
            errors: {
                401: `Unauthorized.`,
                403: `Forbidden. AGENT role required.`,
                404: `Agent profile not found.`,
            },
        });
    }
    /**
     * List assigned sectors for the current agent
     * Returns the list of sectors assigned to the authenticated agent, including sector details and assignment timestamp.
     * @returns AgentSectorsResponse List of assigned sectors.
     * @throws ApiError
     */
    public agentsControllerGetMySectors(): CancelablePromise<AgentSectorsResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/agents/me/sectors',
            errors: {
                401: `Unauthorized.`,
                403: `Forbidden. AGENT role required.`,
                404: `Agent profile not found.`,
            },
        });
    }
    /**
     * Upload a file
     * Upload a file for the authenticated agent profile.
     * @returns any File uploaded successfully.
     * @throws ApiError
     */
    public agentsControllerUploadFile({
        formData,
    }: {
        formData: {
            /**
             * File to upload (JPG, PNG, max 5MB)
             */
            file?: Blob;
        },
    }): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/api/agents/me/upload',
            formData: formData,
            mediaType: 'multipart/form-data',
            errors: {
                400: `No file uploaded or upload failed.`,
                401: `Unauthorized.`,
                403: `Forbidden. AGENT role required.`,
            },
        });
    }
    /**
     * Toggle agent online status
     * Update the agent's online status and optionally their GPS coordinates. When going online, performs a readiness check (status, society validation, documents, sectors). When latitude and longitude are provided, the server automatically resolves the agent's current sector from their position using geofencing.
     * @returns AgentOnlineResponse Online status updated. Response includes the agent profile, the zone de chalandise and lieux à proximité.
     * @throws ApiError
     */
    public agentsControllerToggleOnline({
        requestBody,
    }: {
        requestBody: AgentOnlineDto,
    }): CancelablePromise<AgentOnlineResponse> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/api/agents/me/online',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Agent not ready to go online or validation error.`,
                401: `Unauthorized.`,
                403: `Forbidden. AGENT role required.`,
                404: `Agent profile not found.`,
            },
        });
    }
    /**
     * Get single agent info
     * Returns a single agent profile with user details, society, and sectors. Requires SOCIETY_OWNER (must own the society) or ADMIN role.
     * @returns AgentResponse Agent found.
     * @throws ApiError
     */
    public agentsControllerFindOne({
        id,
    }: {
        /**
         * Agent profile unique identifier
         */
        id: string,
    }): CancelablePromise<AgentResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/agents/{id}',
            path: {
                'id': id,
            },
            errors: {
                401: `Unauthorized.`,
                403: `Forbidden.`,
                404: `Agent not found.`,
            },
        });
    }
    /**
     * Update agent (admin/owner)
     * Update agent information including name, email, phone, birth date, and avatar. Requires SOCIETY_OWNER (must own the society) or ADMIN role.
     * @returns AgentResponse Agent updated successfully.
     * @throws ApiError
     */
    public agentsControllerUpdate({
        id,
        requestBody,
    }: {
        /**
         * Agent profile unique identifier
         */
        id: string,
        requestBody: UpdateAgentDto,
    }): CancelablePromise<AgentResponse> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/api/agents/{id}',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Validation error.`,
                401: `Unauthorized.`,
                403: `Forbidden.`,
                404: `Agent not found.`,
            },
        });
    }
    /**
     * Remove an agent
     * Delete an agent profile. Only the society owner or an admin can remove agents.
     * @returns MessageResponse Agent removed successfully.
     * @throws ApiError
     */
    public agentsControllerRemove({
        id,
    }: {
        /**
         * Agent profile unique identifier
         */
        id: string,
    }): CancelablePromise<MessageResponse> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/api/agents/{id}',
            path: {
                'id': id,
            },
            errors: {
                401: `Unauthorized.`,
                403: `Forbidden.`,
                404: `Agent not found.`,
            },
        });
    }
    /**
     * Assign sectors to an agent
     * Replace the sectors assigned to an agent. Only the society owner can assign sectors.
     * @returns AgentResponse Sectors assigned successfully.
     * @throws ApiError
     */
    public agentsControllerAssignSectors({
        id,
        requestBody,
    }: {
        /**
         * Agent profile unique identifier
         */
        id: string,
        requestBody: AssignSectorsDto,
    }): CancelablePromise<AgentResponse> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/api/agents/{id}/sectors',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Validation error.`,
                401: `Unauthorized.`,
                403: `Forbidden. SOCIETY_OWNER role required.`,
                404: `Agent not found.`,
            },
        });
    }
    /**
     * Suspend an agent
     * Suspend an agent profile, preventing them from going online. The agent status is set to SUSPENDED and their online status is set to false. Only the society owner or an admin can suspend agents.
     * @returns AgentResponse Agent suspended successfully.
     * @throws ApiError
     */
    public agentsControllerSuspend({
        id,
    }: {
        /**
         * Agent profile unique identifier
         */
        id: string,
    }): CancelablePromise<AgentResponse> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/api/agents/{id}/suspend',
            path: {
                'id': id,
            },
            errors: {
                400: `Agent is already suspended.`,
                401: `Unauthorized.`,
                403: `Forbidden. SOCIETY_OWNER or ADMIN role required.`,
                404: `Agent not found.`,
            },
        });
    }
    /**
     * Unsuspend an agent
     * Unsuspend an agent profile, allowing them to go online again. The agent status is set back to OPERATIONAL. Only the society owner or an admin can unsuspend agents.
     * @returns AgentResponse Agent unsuspended successfully.
     * @throws ApiError
     */
    public agentsControllerUnsuspend({
        id,
    }: {
        /**
         * Agent profile unique identifier
         */
        id: string,
    }): CancelablePromise<AgentResponse> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/api/agents/{id}/unsuspend',
            path: {
                'id': id,
            },
            errors: {
                400: `Agent is not suspended.`,
                401: `Unauthorized.`,
                403: `Forbidden. SOCIETY_OWNER or ADMIN role required.`,
                404: `Agent not found.`,
            },
        });
    }
    /**
     * Refuse an agent document
     * Refuse an agent document (pro card or ID card). The document status is set to DENIED. Only the society owner or an admin can refuse agent documents.
     * @returns DocumentResponse Document refused successfully.
     * @throws ApiError
     */
    public agentsControllerRefuseDocument({
        id,
        requestBody,
    }: {
        /**
         * Agent profile unique identifier
         */
        id: string,
        requestBody: RefuseAgentDocumentDto,
    }): CancelablePromise<DocumentResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/api/agents/{id}/documents/refuse',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Invalid document type or document already refused.`,
                401: `Unauthorized.`,
                403: `Forbidden. SOCIETY_OWNER or ADMIN role required.`,
                404: `Agent or document not found.`,
            },
        });
    }
    /**
     * Get agent validation link
     * Returns the validation link for an agent with AWAITING_REGISTRATION status. The link can be shared with the agent to complete their registration. Only the society owner or an admin can access this endpoint.
     * @returns any Validation link returned.
     * @throws ApiError
     */
    public agentsControllerGetValidationLink({
        id,
    }: {
        /**
         * Agent profile unique identifier
         */
        id: string,
    }): CancelablePromise<{
        agentId?: string;
        email?: string;
        firstName?: string;
        lastName?: string;
        validationLink?: string;
        expiresAt?: string;
        status?: string;
    }> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/agents/{id}/validation-link',
            path: {
                'id': id,
            },
            errors: {
                400: `Agent is not in AWAITING_REGISTRATION status or documents not validated.`,
                401: `Unauthorized.`,
                403: `Forbidden. SOCIETY_OWNER or ADMIN role required.`,
                404: `Agent not found.`,
            },
        });
    }
}
